import { createContext, useContext, useRef, useCallback, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';

interface DirtyState {
  isDirty: boolean;
  saveHandler: (() => Promise<void>) | null;
}

interface UnsavedChangesContextValue {
  register: (isDirty: boolean, saveHandler: () => Promise<void>) => void;
  unregister: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

export function useUnsavedChangesContext() {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) {
    throw new Error('useUnsavedChangesContext must be used within UnsavedChangesProvider');
  }
  return ctx;
}

export function UnsavedChangesProvider({ children }: { children: React.ReactNode }) {
  const dirtyRef = useRef<DirtyState>({ isDirty: false, saveHandler: null });
  // Counter to force useBlocker to re-evaluate when dirty state changes
  const [, setTick] = useState(0);

  const register = useCallback((isDirty: boolean, saveHandler: () => Promise<void>) => {
    dirtyRef.current = { isDirty, saveHandler };
    setTick((t) => t + 1);
  }, []);

  const unregister = useCallback(() => {
    dirtyRef.current = { isDirty: false, saveHandler: null };
    setTick((t) => t + 1);
  }, []);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return dirtyRef.current.isDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  return (
    <UnsavedChangesContext.Provider value={{ register, unregister }}>
      {children}
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onSaveAndContinue={async () => {
          if (dirtyRef.current.saveHandler) {
            try {
              await dirtyRef.current.saveHandler();
              blocker.proceed?.();
            } catch {
              // Save failed — stay on page. The page's own error handling (toast) will inform the user.
            }
          }
        }}
        onDiscard={() => {
          blocker.proceed?.();
        }}
        onGoBack={() => {
          blocker.reset?.();
        }}
      />
    </UnsavedChangesContext.Provider>
  );
}
